import styled, { createGlobalStyle } from "styled-components";
import WithDirection from "@iso/lib/helpers/rtl";

// Modals portal to document.body, so their focus styles cannot be scoped
// via the FileManagerWrapper styled component. This global block targets
// the wrapClassName we set on the assign-user / set-expiry modals to
// suppress the project-wide yellow focus glow inherited from globalStyle.js.
export const FileManagerModalGlobalStyles = createGlobalStyle`
  .file-manager-bulk-modal {
    .ant-input:focus,
    .ant-input-focused,
    .ant-input-affix-wrapper-focused,
    .ant-select-focused .ant-select-selector,
    .ant-select-selector:focus,
    .ant-picker:hover,
    .ant-picker-focused {
      box-shadow: 0 0 0 2px rgba(11, 42, 27, 0.15) !important;
      border-color: #0b2a1b !important;
    }
  }
`;

const FileManagerWrapper = styled.div`
  width: 100%;
  height: 100%;

  .manage-property-header {
    padding: 20px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: auto;
  }

  .manage-property-title {
    font-size: 18px;
    font-weight: 600;
    color: #101828;
  }

  .file-manager-content {
    padding: 0 24px 24px;
  }

  .file-manager-toolbar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .bulk-action-bar {
    position: sticky;
    bottom: 0;
    z-index: 10;
    margin-top: 12px;
    padding: 10px 14px;
    background: #0b2a1b;
    color: #fff;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);

    .bulk-count {
      font-weight: 600;
      margin-right: 8px;
    }

    .ant-btn {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.3);
      color: #fff;

      &:hover,
      &:focus {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.5);
        color: #fff;
      }

      &.ant-btn-dangerous {
        background: #c43d3d;
        border-color: #c43d3d;
      }
    }
  }

  /* Scope tabs (My Files / Corporate Library) */
  > .isoBoxWrapper > .file-manager-content > .ant-tabs > .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active {
    border-bottom-color: #fff;
  }

  /* Inner tabs (Library / Upload) */
  .ant-tabs-content {
    padding-top: 16px;
  }

  .folder-breadcrumb-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #fafafa;
    border-radius: 6px;

    .ant-breadcrumb a {
      color: #0b2a1b;
    }
  }

  .folder-card {
    border: 2px solid #e8e8e8;
    border-radius: 8px;
    padding: 12px 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    position: relative;

    &:hover {
      border-color: #0b2a1b;
      background-color: rgba(11, 42, 27, 0.02);
    }

    .folder-actions {
      position: absolute;
      top: 4px;
      right: 4px;
      opacity: 0;
      transition: opacity 0.2s;
    }

    &:hover .folder-actions {
      opacity: 1;
    }

    .folder-icon {
      font-size: 36px;
      color: #faad14;
      margin-bottom: 6px;
    }

    .folder-name {
      font-size: 12px;
      font-weight: 500;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .folder-meta {
      font-size: 11px;
      color: #999;
      margin-top: 2px;
    }
  }

  .search-filter-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;

    .file-search-input {
      flex: 1;
      border-radius: 8px;
      height: 40px;

      input {
        border: none !important;
        &:focus {
          box-shadow: none;
        }
      }

      &.ant-input-affix-wrapper-focused,
      &:hover {
        box-shadow: none;
        border-color: #0b2a1b;
      }
    }

    .type-filter {
      min-width: 150px;
      height: 40px;

      .ant-select-selector {
        height: 40px !important;
        border-radius: 8px !important;
        display: flex;
        align-items: center;
      }
    }

    .sort-select {
      min-width: 200px;
      height: 40px;

      .ant-select-selector {
        height: 40px !important;
        border-radius: 8px !important;
        display: flex;
        align-items: center;
      }
    }

    .view-mode-toggle {
      display: inline-flex;
      align-items: center;
      height: 40px;

      .ant-radio-button-wrapper {
        height: 40px;
        line-height: 38px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0 12px;
      }

      .ant-radio-button-wrapper-checked {
        background: #0b2a1b;
        border-color: #0b2a1b;
        color: #fff;

        &:hover {
          color: #fff;
        }
      }
    }
  }

  .library-grid {
    min-height: 300px;
  }

  /* List-view table */
  .file-list-table {
    .ant-table {
      border-radius: 8px;
    }

    .ant-table-thead > tr > th {
      background: #fafafa;
      font-weight: 600;
      color: #555;
    }

    .ant-table-row:hover {
      background: rgba(11, 42, 27, 0.04);
    }

    .file-list-name {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;

      .file-list-icon {
        flex: 0 0 auto;
        font-size: 18px;
        color: #888;
        display: inline-flex;
      }

      .file-list-name-text {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .file-list-actions {
      display: inline-flex;
      gap: 2px;
      justify-content: flex-end;
    }
  }

  .file-card {
    border: 2px solid #e8e8e8;
    border-radius: 8px;
    padding: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 12px;
    position: relative;

    &:hover {
      border-color: #0b2a1b;
    }

    &.selected {
      border-color: #0b2a1b;
      background-color: rgba(11, 42, 27, 0.04);
    }

    &.bulk-selected {
      border-color: #0b2a1b;
      background-color: rgba(11, 42, 27, 0.08);
      box-shadow: 0 0 0 1px #0b2a1b inset;
    }

    .file-meta-row {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-top: 4px;
    }

    .file-meta-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      color: #555;
      background: #f0f1f3;
      border-radius: 10px;
      padding: 2px 8px;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.is-expired {
        color: #fff;
        background: #c43d3d;
      }
    }

    .file-thumbnail {
      width: 100%;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 4px;
      background-color: #fafafa;
      margin-bottom: 8px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .file-type-icon {
        font-size: 40px;
        color: #999;
      }
    }

    .file-name {
      font-size: 12px;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-type-badge {
      font-size: 11px;
      color: #999;
      text-transform: uppercase;
    }

    .selection-indicator {
      position: absolute;
      top: 8px;
      right: 8px;
    }

    /* File action buttons overlay */
    .file-card-actions {
      position: absolute;
      top: 8px;
      right: 8px;
      display: flex;
      gap: 2px;
      opacity: 0;
      transition: opacity 0.2s;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 4px;
      padding: 2px;
      z-index: 1;

      .ant-btn {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &:hover .file-card-actions {
      opacity: 1;
    }
  }

  .pagination-bar {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }

  .upload-tab-content {
    padding: 16px 0;

    .upload-hint {
      margin-bottom: 16px;
    }

    .ant-alert-info {
      background-color: #fff;
    }

    .upload-drop-zone {
      border: 2px dashed #d9d9d9;
      border-radius: 8px;
      padding: 40px 20px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      background: #fafafa;

      &:hover {
        border-color: #0b2a1b;
        background: rgba(11, 42, 27, 0.02);
      }

      &.drag-over {
        border-color: #0b2a1b;
        background: rgba(11, 42, 27, 0.06);
        border-style: solid;
      }

      &.uploading {
        cursor: default;
        opacity: 0.7;
      }

      .drop-zone-icon {
        font-size: 48px;
        color: #0b2a1b;
        margin-bottom: 12px;
      }

      .drop-zone-title {
        font-size: 16px;
        font-weight: 500;
        color: #333;
        margin-bottom: 4px;
      }

      .drop-zone-subtitle {
        font-size: 13px;
        color: #999;
        margin: 0;
      }
    }

    .folder-upload-progress {
      margin-top: 16px;

      .folder-upload-list {
        max-height: 200px;
        overflow-y: auto;
        margin-top: 8px;
        background:
          linear-gradient(white 30%, rgba(255, 255, 255, 0)) center top,
          linear-gradient(rgba(255, 255, 255, 0), white 70%) center bottom,
          radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)) center top,
          radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)) center bottom;
        background-repeat: no-repeat;
        background-size: 100% 40px, 100% 40px, 100% 10px, 100% 10px;
        background-attachment: local, local, scroll, scroll;

        .ant-list-item {
          padding: 4px 0;
          gap: 8px;
        }

        .folder-upload-filename {
          font-size: 12px;
          color: #666;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  /* Preview modal content */
  .preview-image-container img {
    max-width: 100%;
    max-height: 70vh;
    height: auto;
    display: block;
    margin: 0 auto;
    border-radius: 4px;
    object-fit: contain;
  }

  @media only screen and (max-width: 768px) {
    .manage-property-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .file-manager-content {
      padding: 0 16px 16px;
    }
  }
`;

export default WithDirection(FileManagerWrapper);
